import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { cinemas } from "@/mocks/cinemas";

interface SuggestionItem {
  id: string;
  label: string;
  sub: string;
  path: string;
  type: "电影" | "影院";
}

export default function SearchBox({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<SuggestionItem[]>([]);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) {
      setItems([]);
      return;
    }
    const movieItems: SuggestionItem[] = movies
      .filter((m) => m.title.toLowerCase().includes(q))
      .slice(0, 5)
      .map((m) => ({
        id: `m-${m.id}`,
        label: m.title,
        sub: `${m.genres.join(" / ")} · ${m.year}`,
        path: `/movie/${m.id}`,
        type: "电影",
      }));
    const cinemaItems: SuggestionItem[] = cinemas
      .filter((c) => c.name.toLowerCase().includes(q) || c.city.includes(q))
      .slice(0, 4)
      .map((c) => ({
        id: `c-${c.id}`,
        label: c.name,
        sub: `${c.city} · ${c.district}`,
        path: `/cinema/${c.id}`,
        type: "影院",
      }));
    setItems([...movieItems, ...cinemaItems]);
  }, [keyword]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const goSearch = () => {
    const q = keyword.trim();
    if (!q) return;
    navigate(`/movies?q=${encodeURIComponent(q)}`);
    setOpen(false);
    onNavigate?.();
  };

  return (
    <div ref={boxRef} className="relative w-full">
      <div className="flex items-center gap-2 rounded-full border border-background-300/70 bg-background-50 px-3 py-1.5">
        <span className="flex h-6 w-6 items-center justify-center text-foreground-500">
          <i className="ri-search-line text-base"></i>
        </span>
        <input
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") goSearch();
          }}
          placeholder="搜索电影名 / 影院名"
          className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
        />
        {keyword && (
          <button
            type="button"
            onClick={() => setKeyword("")}
            className="flex h-5 w-5 items-center justify-center text-foreground-400 hover:text-foreground-700"
            aria-label="清空"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        )}
      </div>

      {open && keyword.trim() && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-80 overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-2 shadow-lg">
          {items.length === 0 ? (
            <div className="px-4 py-3 text-sm text-foreground-500">
              未找到「{keyword}」相关内容，换个关键词试试
            </div>
          ) : (
            <>
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    navigate(item.path);
                    setOpen(false);
                    setKeyword("");
                    onNavigate?.();
                  }}
                  className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-background-100"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground-900">
                      {item.label}
                    </span>
                    <span className="block truncate text-xs text-foreground-500">
                      {item.sub}
                    </span>
                  </span>
                  <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                    {item.type}
                  </span>
                </button>
              ))}
              <button
                type="button"
                onClick={goSearch}
                className="mt-1 w-full border-t border-background-200 px-4 py-2 text-left text-sm text-primary-600 hover:bg-background-100"
              >
                查看「{keyword}」的全部电影结果 →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}